<script setup lang="ts">
import axios from 'axios'
import { Loader2, MoreHorizontal } from 'lucide-vue-next'

import BaseChip, { type ChipColor } from '@/components/base/BaseChip.vue'
import ImportDetailModal from '@/views/import/Components/ImportDetailModal.vue'
import type { Audience, CustomerImportDiagnostics, CustomerImportListItem, CustomerImportStage } from '@/types/types'
const { t } = useI18n()
const { formatNumber } = useNumber()
const { isSupportConnection } = useLocalStorage()
const notification = useNotification()
const { fetchMerchantAudiences } = useAudience()

const isConfirmationModalOpen = ref(false)
const isDeleteConfirmationModalOpen = ref(false)
const isDiagnosticsModalOpen = ref(false)
const isAssignToListModalOpen = ref(false)
const selectedImportId = ref<number | null>(null)
const selectedMerchantSegmentId = ref<number | null>(null)
const audiences = ref<Audience[]>([])
const isProcessing = ref(false)
const isLoadingDiagnostics = ref(false)
const isLoadingAudiences = ref(false)
const diagnostics = ref<CustomerImportDiagnostics | null>(null)
const diagnosticsPollTimer = ref<ReturnType<typeof setInterval> | null>(null)

const DIAGNOSTICS_POLL_INTERVAL_MS = 4000

const stopDiagnosticsPolling = (): void => {
  if (diagnosticsPollTimer.value !== null) {
    clearInterval(diagnosticsPollTimer.value)
    diagnosticsPollTimer.value = null
  }
}

const fetchDiagnostics = async (importId: number, initial = false): Promise<boolean> => {
  const { data } = await axios.get(`customer-imports/${importId}/diagnostics`, {
    params: initial ? { initial: 1 } : undefined
  })

  diagnostics.value = data.data

  return !data.data.isComplete
}

const startDiagnosticsPolling = (importId: number): void => {
  stopDiagnosticsPolling()

  diagnosticsPollTimer.value = setInterval(async () => {
    if (!isDiagnosticsModalOpen.value || selectedImportId.value !== importId) {
      stopDiagnosticsPolling()

      return
    }

    try {
      const shouldContinue = await fetchDiagnostics(importId)

      if (!shouldContinue) {
        stopDiagnosticsPolling()
      }
    } catch {
      stopDiagnosticsPolling()
    }
  }, DIAGNOSTICS_POLL_INTERVAL_MS)
}

const { segmentTypes } = useAudience()
const isLoaded = ref(false)

const pageTitle = ref({
  title: 'pages.import.historyPage.title',
  description: 'pages.import.historyPage.description'
})

const customerImports = ref<CustomerImportListItem[]>([])

const loadProcessingImports = ref(true)
const table = ref({
  pagination: {
    currentPage: 1,
    lastPage: 1,
    total: 1
  },
  columns: {
    name: {
      label: t('pages.import.historyPage.name'),
      headerClass: 'whitespace-nowrap'
    },
    status: {
      label: t('pages.import.historyPage.status'),
      headerClass: 'whitespace-nowrap'
    },
    createdAt: {
      label: t('pages.import.historyPage.createdAt'),
      headerClass: 'whitespace-nowrap'
    },
    importedBy: {
      label: t('pages.import.historyPage.importedBy'),
      headerClass: 'whitespace-nowrap'
    },
    addedContacts: {
      label: t('pages.import.historyPage.addedContacts'),
      headerClass: 'whitespace-nowrap w-auto!',
      numeric: true
    },
    updatedContacts: {
      label: t('pages.import.historyPage.updatedContacts'),
      headerClass: 'whitespace-nowrap w-auto!',
      numeric: true
    },
    ignoredContacts: {
      label: t('pages.import.historyPage.ignoredContacts'),
      headerClass: 'whitespace-nowrap w-auto!',
      numeric: true
    },
    merchantSegment: {
      label: t('pages.import.historyPage.merchantSegment'),
      headerClass: 'whitespace-nowrap w-auto!'
    },
    diagnostics: {
      label: '',
      headerClass: 'whitespace-nowrap w-auto!',
      align: 'right' as const
    },
    actions: {
      label: '',
      headerClass: 'whitespace-nowrap w-auto!',
      align: 'right' as const
    }
  }
})

const STAGE_COLORS: Record<CustomerImportStage, ChipColor> = {
  pending: 'slate',
  parsing: 'yellow',
  processing: 'yellow',
  complete: 'emerald'
}

const resolveStage = (data: CustomerImportListItem): CustomerImportStage => {
  if (data.stage) {
    return data.stage
  }

  if (data.isComplete) {
    return 'complete'
  }

  if (data.customerImport.startedAt) {
    return 'processing'
  }

  return data.percentage > 0 ? 'parsing' : 'pending'
}

const percentageOf = (done: number, total: number): number => {
  return total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
}

const formatDuration = (minutes: number): string => {
  if (minutes >= 1440) {
    return t('pages.import.historyPage.duration.days', { count: Math.floor(minutes / 1440) })
  }

  if (minutes >= 60) {
    return t('pages.import.historyPage.duration.hours', { count: Math.floor(minutes / 60) })
  }

  return t('pages.import.historyPage.duration.minutes', { count: minutes })
}

const formatDateTime = (value: string | null): string | null => {
  if (!value) {
    return null
  }

  const date = new Date(value.replace(' ', 'T'))

  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
}

const getImportStatus = (data: CustomerImportListItem): { label: string; color: ChipColor } => {
  if (data.isStalled) {
    return {
      label: t('pages.import.historyPage.stalled'),
      color: 'red'
    }
  }

  const stage = resolveStage(data)

  if (stage === 'pending') {
    return { label: t('pages.import.historyPage.pending'), color: STAGE_COLORS.pending }
  }

  if (stage === 'parsing') {
    return {
      label: t('pages.import.historyPage.analyze', {
        percentage: percentageOf(data.parsedLines, data.expectedLines)
      }),
      color: STAGE_COLORS.parsing
    }
  }

  if (stage === 'processing') {
    return {
      label: t('pages.import.historyPage.inProgress', {
        percentage: percentageOf(data.totalCustomersProcessed, data.totalCustomers)
      }),
      color: STAGE_COLORS.processing
    }
  }

  return { label: t('pages.import.historyPage.completed'), color: STAGE_COLORS.complete }
}

const getStatusDetails = (data: CustomerImportListItem): string[] => {
  const stage = resolveStage(data)
  const details = [
    t('pages.import.historyPage.statusTooltip.stage', {
      stage: t(`pages.import.historyPage.stages.${stage}`)
    })
  ]

  if (!data.isLegacy) {
    details.push(
      t('pages.import.historyPage.statusTooltip.parsedLines', {
        parsed: data.parsedLines,
        total: data.expectedLines
      })
    )

    if (stage === 'processing' || stage === 'complete') {
      details.push(
        t('pages.import.historyPage.statusTooltip.processedContacts', {
          processed: data.totalCustomersProcessed,
          total: data.totalCustomers
        })
      )
    }
  }

  const lastActivityAt = formatDateTime(data.lastActivityAt)

  if (lastActivityAt) {
    details.push(t('pages.import.historyPage.statusTooltip.lastActivity', { date: lastActivityAt }))
  }

  if (data.isStalled && data.stalledForMinutes) {
    details.push(
      t('pages.import.historyPage.statusTooltip.stalledSince', {
        duration: formatDuration(data.stalledForMinutes)
      })
    )
  }

  return details
}

const DELETE_AFTER_INACTIVITY_MINUTES = 24 * 60

const inactiveForMinutes = (lastActivityAt: string | null): number | null => {
  if (!lastActivityAt) {
    return null
  }

  const lastActivity = new Date(lastActivityAt.replace(' ', 'T'))

  return Math.floor((Date.now() - lastActivity.getTime()) / 60000)
}

const canDeleteImport = (data: CustomerImportListItem): boolean => {
  if (data.isLegacy || data.isComplete || data.customerImport.startedAt) {
    return false
  }

  const inactiveMinutes = inactiveForMinutes(data.lastActivityAt)

  return inactiveMinutes !== null && inactiveMinutes >= DELETE_AFTER_INACTIVITY_MINUTES
}

const canDisablePoints = (data: CustomerImportListItem): boolean => {
  if (data.isLegacy || !data.customerImport.startedAt) {
    return false
  }

  const addedContacts = data.newCustomersCount

  return addedContacts > 0 || data.existedCustomersCount > 0
}

const canAssignToList = (data: CustomerImportListItem): boolean => canDisablePoints(data)

const tableRows = computed(() => {
  return customerImports.value.map((data) => {
    return {
      id: data.customerImport.id,
      data: {
        rowId: data.customerImport.id,
        id: isSupportConnection.value ? data.customerImport.id : '',
        name: data.customerImport.name,
        status: getImportStatus(data),
        statusDetails: getStatusDetails(data),
        createdAt: data.customerImport?.startedAt
          ? {
              date: new Date(data.customerImport.startedAt)?.toLocaleDateString(),
              time: new Date(data.customerImport.startedAt)?.toLocaleTimeString()
            }
          : null,
        isPending: data.isPending,
        importedBy: data.customerImport.userName,
        addedContacts: !data.customerImport.startedAt ? '-' : data.newCustomersCount,
        updatedContacts: data.isLegacy || !data.customerImport.startedAt ? '-' : data.existedCustomersCount,
        ignoredContacts: data.isLegacy || !data.customerImport.startedAt ? '-' : data.totalCustomersWithError,
        merchantSegment: data.merchantSegment,
        canDelete: canDeleteImport(data),
        canDisablePoints: canDisablePoints(data),
        canAssignToList: canAssignToList(data),
        hasActions:
          canAssignToList(data) || (isSupportConnection.value && (canDisablePoints(data) || canDeleteImport(data)))
      }
    }
  })
})

const isImportSettled = (importItem: CustomerImportListItem) => importItem.isComplete || importItem.isStalled

const isAllImportsSettled = (imports: CustomerImportListItem[]) => imports.every(isImportSettled)

const getCustomerImports = async (pageNumber?: number) => {
  const { data } = await axios.get('customer-imports', {
    params: {
      page: pageNumber || table.value.pagination.currentPage,
      pageSize: 10
    }
  })
  customerImports.value = data.data

  table.value.pagination.currentPage = data.meta.current_page
  table.value.pagination.lastPage = data.meta.last_page
  table.value.pagination.total = data.meta.total
}

const stopProcessingImports = async () => {
  loadProcessingImports.value = false
  await getCustomerImports()
}

const getProcessingImports = async () => {
  try {
    let isComplete = false
    while (!isComplete && loadProcessingImports.value) {
      const { data } = await axios.get('customer-imports', {
        params: {
          page: table.value?.pagination?.currentPage ?? 1,
          pageSize: 10
        }
      })
      isComplete = isAllImportsSettled(data.data)

      customerImports.value = data.data

      table.value.pagination.currentPage = data.meta.current_page
      table.value.pagination.lastPage = data.meta.last_page
      table.value.pagination.total = data.meta.total

      if (isComplete) {
        stopProcessingImports()
      } else {
        await new Promise((resolve) => setTimeout(resolve, 4000))
      }
    }
  } catch {
    stopProcessingImports()
  }
}

onMounted(async () => {
  await getCustomerImports()
  isLoaded.value = true

  if (!isAllImportsSettled(customerImports.value)) {
    await getProcessingImports()
  }
})

onUnmounted(() => {
  stopProcessingImports()
  stopDiagnosticsPolling()
})

const getMenuItems = (
  importId: number,
  canDelete: boolean,
  canDisablePointsValue: boolean,
  canAssignToListValue: boolean
) => {
  const items: { label: string; command: () => void }[] = []

  if (canAssignToListValue) {
    items.push({
      label: t('pages.import.historyPage.actionsModal.assignToList'),
      command: () => openAssignToListModal(importId)
    })
  }

  if (isSupportConnection.value) {
    if (canDisablePointsValue) {
      items.push({
        label: t('pages.import.historyPage.actionsModal.disablePoints'),
        command: () => {
          selectedImportId.value = importId
          openConfirmationModal()
        }
      })
    }

    if (canDelete) {
      items.push({
        label: t('pages.import.historyPage.actionsModal.delete'),
        command: () => {
          selectedImportId.value = importId
          isDeleteConfirmationModalOpen.value = true
        }
      })
    }
  }

  return items
}

const loadAudiences = async () => {
  isLoadingAudiences.value = true
  try {
    const data = await fetchMerchantAudiences({ pagination: { pageSize: 1000, page: 1 }, type: 'static' })
    audiences.value = data.data
  } catch {
    audiences.value = []
  } finally {
    isLoadingAudiences.value = false
  }
}

const openAssignToListModal = async (importId: number) => {
  selectedImportId.value = importId
  selectedMerchantSegmentId.value = null
  isAssignToListModalOpen.value = true
  await loadAudiences()
}

const openDiagnostics = async (importId: number) => {
  selectedImportId.value = importId
  diagnostics.value = null
  isDiagnosticsModalOpen.value = true
  isLoadingDiagnostics.value = true
  stopDiagnosticsPolling()

  try {
    const shouldPoll = await fetchDiagnostics(importId, true)

    if (shouldPoll) {
      startDiagnosticsPolling(importId)
    }
  } catch {
    notification.create('error', {
      text: t('pages.import.historyPage.diagnosticsModal.loadError')
    })
    isDiagnosticsModalOpen.value = false
  } finally {
    isLoadingDiagnostics.value = false
  }
}

const downloadImportFile = async (importId: number, fileName: string) => {
  try {
    const { data } = await axios.get(`customer-imports/${importId}/file`, { responseType: 'blob' })
    const fileURL = window.URL.createObjectURL(new Blob([data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    fileLink.setAttribute('download', fileName)
    document.body.appendChild(fileLink)
    fileLink.click()
    fileLink.remove()
    URL.revokeObjectURL(fileURL)

    notification.create('success', {
      text: t('global.download.success'),
      timeout: 2000
    })
  } catch {
    notification.create('error', {
      text: t('pages.import.historyPage.notifications.fileDownloadError')
    })
  }
}

const downloadImportErrors = async (importId: number, fileName: string) => {
  try {
    const { data } = await axios.get(`customer-imports/${importId}/errors/export`, { responseType: 'blob' })
    const fileURL = window.URL.createObjectURL(new Blob([data]))
    const fileLink = document.createElement('a')
    const baseName = fileName.replace(/\.[^.]+$/, '')

    fileLink.href = fileURL
    fileLink.setAttribute('download', `${baseName}-errors.csv`)
    document.body.appendChild(fileLink)
    fileLink.click()
    fileLink.remove()
    URL.revokeObjectURL(fileURL)

    notification.create('success', {
      text: t('pages.import.historyPage.diagnosticsModal.errorsExportSuccess'),
      timeout: 2000
    })
  } catch {
    notification.create('error', {
      text: t('pages.import.historyPage.diagnosticsModal.errorsExportError')
    })
  }
}

const openConfirmationModal = () => {
  isConfirmationModalOpen.value = true
}

const closeModals = () => {
  isConfirmationModalOpen.value = false
  isDeleteConfirmationModalOpen.value = false
  isDiagnosticsModalOpen.value = false
  isAssignToListModalOpen.value = false
  stopDiagnosticsPolling()
  diagnostics.value = null
  selectedImportId.value = null
  selectedMerchantSegmentId.value = null
}

const assignToList = async () => {
  if (!selectedImportId.value || !selectedMerchantSegmentId.value) return

  isProcessing.value = true
  try {
    await axios.post(`customer-imports/${selectedImportId.value}/segments`, {
      merchantSegmentId: selectedMerchantSegmentId.value
    })
    notification.create('success', {
      text: t('pages.import.historyPage.notifications.assignToListQueued')
    })
    closeModals()
    await getCustomerImports()
  } catch {
    notification.create('error', {
      text: t('pages.import.historyPage.notifications.assignToListError')
    })
  } finally {
    isProcessing.value = false
  }
}

const disablePoints = async () => {
  if (!selectedImportId.value) return

  isProcessing.value = true
  try {
    await axios.post('customer-imports/' + selectedImportId.value + '/points/disable')
    notification.create('success', {
      text: t('pages.import.historyPage.notifications.pointsDisablingQueued')
    })
    closeModals()
    await getCustomerImports()
  } catch {
    notification.create('error', {
      text: t('pages.import.historyPage.notifications.pointsDisablingError')
    })
  } finally {
    isProcessing.value = false
  }
}

const deleteImport = async () => {
  if (!selectedImportId.value) return

  isProcessing.value = true
  try {
    await axios.delete('customer-imports/' + selectedImportId.value)
    notification.create('success', {
      text: t('pages.import.historyPage.notifications.importDeleted')
    })
    closeModals()
    await getCustomerImports()
  } catch {
    notification.create('error', {
      text: t('pages.import.historyPage.notifications.importDeleteError')
    })
  } finally {
    isProcessing.value = false
  }
}
</script>

<template>
  <div v-if="isLoaded" class="flex flex-col">
    <TheListHeader :title="t(pageTitle.title)" />
    <TheEmptyState
      v-if="tableRows.length === 0"
      module="import"
      :title="t(`pages.import.emptyState.title`)"
      :description="t(`pages.import.emptyState.description`)"
    />
    <div v-else class="px-6 pb-6 sm:px-12 sm:pb-8">
      <AppTable
        :columns="table.columns"
        :rows="tableRows"
        :pagination="table.pagination"
        @update:change-page="getCustomerImports($event)"
      >
        <template #td-name="{ data }">
          <span class="font-medium text-foreground">{{ data.name }} </span>
        </template>
        <template #td-status="{ data }">
          <AppTooltip tooltip-position="top-start" width="max-w-xs">
            <BaseChip :color="data.status.color" :text="data.status.label" />
            <template #tooltip-content>
              <div class="flex flex-col gap-0.5 text-left">
                <span v-for="detail in data.statusDetails" :key="detail">{{ detail }}</span>
              </div>
            </template>
          </AppTooltip>
        </template>
        <template #td-createdAt="{ data }">
          <div v-if="!data.isPending && data.createdAt" class="flex flex-col">
            <div class="text-sm font-medium text-foreground">{{ data.createdAt.date }}</div>
            <div class="text-sm text-muted-foreground">{{ data.createdAt.time }}</div>
          </div>
          <div v-else class="flex flex-col text-sm font-medium text-foreground">-</div>
        </template>
        <template #td-importedBy="{ data }">
          <span class="font-medium text-foreground">
            {{ data.importedBy }}
          </span>
        </template>
        <template #td-addedContacts="{ data }">
          <template v-if="!data.isPending">{{ formatNumber(data.addedContacts) }}</template>
          <template v-else>-</template>
        </template>
        <template #td-updatedContacts="{ data }">
          <template v-if="!data.isPending">{{ formatNumber(data.updatedContacts) }}</template>
          <template v-else>-</template>
        </template>
        <template #td-ignoredContacts="{ data }">
          <template v-if="!data.isPending">
            {{ formatNumber(data.ignoredContacts) }}
          </template>
          <template v-else>-</template>
        </template>
        <template #td-merchantSegment="{ data }">
          <span class="font-medium text-foreground">
            <BaseChip
              v-if="data.merchantSegment"
              :color="segmentTypes.find((type) => type.id === 'static')?.color"
              :icon="segmentTypes.find((type) => type.id === 'static')?.icon"
              :text="data.merchantSegment"
            />
            <span v-else class="text-muted-foreground">-</span>
          </span>
        </template>
        <template #td-diagnostics="{ data }">
          <button
            type="button"
            class="text-primary underline decoration-dotted hover:text-primary/80"
            @click="openDiagnostics(data.rowId)"
          >
            {{ t('pages.import.historyPage.detail') }}
          </button>
        </template>
        <template #td-actions="{ data }">
          <div v-if="data.hasActions" class="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <button
                  type="button"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground-secondary hover:bg-accent focus:outline-hidden"
                >
                  <MoreHorizontal class="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  v-for="item in getMenuItems(data.rowId, data.canDelete, data.canDisablePoints, data.canAssignToList)"
                  :key="item.label"
                  @click="item.command"
                >
                  {{ item.label }}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </template>
      </AppTable>
    </div>
  </div>
  <TheLoadingPage v-else />

  <BaseModal
    :is-open="isDiagnosticsModalOpen"
    size="4xl"
    :heading-title="t('pages.import.historyPage.diagnosticsModal.title')"
    @close="closeModals"
  >
    <ImportDetailModal
      :import-id="selectedImportId"
      :diagnostics="diagnostics"
      :is-loading="isLoadingDiagnostics"
      :is-support-connection="isSupportConnection"
      @download="downloadImportFile"
      @export-errors="downloadImportErrors"
    />

    <template #linkBtn>
      <div class="flex items-center justify-end">
        <Button variant="outline" type="button" @click="closeModals">
          {{ t('global.words.close') }}
        </Button>
      </div>
    </template>
  </BaseModal>

  <BaseModal
    :is-open="isAssignToListModalOpen"
    :heading-title="t('pages.import.historyPage.assignToListModal.title')"
    @close="closeModals"
  >
    <div class="mb-6 space-y-4 text-foreground-secondary">
      <div>
        {{ t('pages.import.historyPage.assignToListModal.message') }}
      </div>
      <div>
        <div class="mb-2 text-sm text-foreground">
          {{ t('pages.import.historyPage.assignToListModal.inputLabel') }}
        </div>
        <div v-if="isLoadingAudiences" class="flex items-center gap-2 text-sm">
          <Loader2 class="h-4 w-4 animate-spin" />
        </div>
        <DropdownAudience
          v-else
          v-model="selectedMerchantSegmentId"
          :audiences="audiences"
          type="static"
          creation-allowed
          :placeholder="t('global.multiselect.audiences.placeholderCreateAllowed')"
          :filter-placeholder="t('global.multiselect.audiences.filterPlaceholderCreateAllowed')"
          :default-empty-message="t('global.multiselect.audiences.noLists')"
          @audience-created="loadAudiences"
        />
      </div>
    </div>
    <template #linkBtn>
      <div class="flex items-center justify-end gap-4">
        <Button variant="outline" type="button" @click="closeModals">
          {{ t('global.words.cancel') }}
        </Button>
        <Button
          variant="default-primary"
          :disabled="isProcessing || !selectedMerchantSegmentId"
          type="button"
          @click="assignToList"
        >
          <Loader2 v-if="isProcessing" class="h-4 w-4 animate-spin" />
          {{ t('global.words.confirm') }}
        </Button>
      </div>
    </template>
  </BaseModal>

  <BaseModal
    :is-open="isConfirmationModalOpen"
    :heading-title="t('pages.import.historyPage.confirmModal.title') + ' #' + selectedImportId"
    @close="closeModals"
  >
    <div class="mb-6 text-foreground-secondary">
      <div>
        {{ t('pages.import.historyPage.confirmModal.message') }}
      </div>
    </div>
    <template #linkBtn>
      <div class="flex items-center justify-end gap-4">
        <Button variant="outline" type="button" @click="closeModals">
          {{ t('global.words.cancel') }}
        </Button>
        <Button variant="default-primary" :disabled="isProcessing" type="button" @click="disablePoints">
          <Loader2 v-if="isProcessing" class="h-4 w-4 animate-spin" />
          {{ t('global.words.confirm') }}
        </Button>
      </div>
    </template>
  </BaseModal>

  <BaseModal
    :is-open="isDeleteConfirmationModalOpen"
    :heading-title="t('pages.import.historyPage.deleteConfirmModal.title') + ' #' + selectedImportId"
    @close="closeModals"
  >
    <div class="mb-6 text-foreground-secondary">
      <div>
        {{ t('pages.import.historyPage.deleteConfirmModal.message') }}
      </div>
    </div>
    <template #linkBtn>
      <div class="flex items-center justify-end gap-4">
        <Button variant="outline" type="button" @click="closeModals">
          {{ t('global.words.cancel') }}
        </Button>
        <Button variant="default-primary" :disabled="isProcessing" type="button" @click="deleteImport">
          <Loader2 v-if="isProcessing" class="h-4 w-4 animate-spin" />
          {{ t('global.words.confirm') }}
        </Button>
      </div>
    </template>
  </BaseModal>
</template>
